// ============================================================================
// GitHub 연동 React Query Hooks (CHG-046)
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as githubApi from '../services/githubService';
import type { ConnectRepoData, UpdateRepoData } from '../services/githubService';

import { useOrgStore } from '../store/orgStore';

export const githubKeys = {
  repos: (projectId: string) => ['github', 'repos', projectId] as const,
  statusMappings: (projectId: string) => ['github', 'status-mappings', projectId] as const,
  userProfile: (orgId?: string | null) => ['github', 'user-profile', orgId ?? 'none'] as const,
};

// --- Repository hooks ---

export function useGitHubRepos(projectId: string) {
  return useQuery({
    queryKey: githubKeys.repos(projectId),
    queryFn: () => githubApi.fetchGitHubRepos(projectId),
    enabled: !!projectId,
  });
}

export function useConnectRepo(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConnectRepoData) => githubApi.connectGitHubRepo(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: githubKeys.repos(projectId) });
    },
  });
}

export function useUpdateRepo(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ repoId, data }: { repoId: string; data: UpdateRepoData }) =>
      githubApi.updateGitHubRepo(projectId, repoId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: githubKeys.repos(projectId) });
    },
  });
}

export function useDisconnectRepo(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (repoId: string) => githubApi.disconnectGitHubRepo(projectId, repoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: githubKeys.repos(projectId) });
    },
  });
}

export function useTestConnection(projectId: string) {
  return useMutation({
    mutationFn: (repoId: string) => githubApi.testGitHubConnection(projectId, repoId),
  });
}

export function useSyncRepo(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (repoId: string) => githubApi.syncGitHubRepo(projectId, repoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: githubKeys.repos(projectId) });
    },
  });
}

// --- Issue-level GitHub data hooks ---

export const issueGitHubKeys = {
  branches: (projectId: string, issueId: string) => ['github', 'branches', projectId, issueId] as const,
  commits: (projectId: string, issueId: string) => ['github', 'commits', projectId, issueId] as const,
  prs: (projectId: string, issueId: string) => ['github', 'prs', projectId, issueId] as const,
};

export function useIssueBranches(projectId: string, issueId: string) {
  return useQuery({
    queryKey: issueGitHubKeys.branches(projectId, issueId),
    queryFn: () => githubApi.fetchIssueBranches(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

export function useIssueCommits(projectId: string, issueId: string) {
  return useQuery({
    queryKey: issueGitHubKeys.commits(projectId, issueId),
    queryFn: () => githubApi.fetchIssueCommits(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

export function useIssuePullRequests(projectId: string, issueId: string) {
  return useQuery({
    queryKey: issueGitHubKeys.prs(projectId, issueId),
    queryFn: () => githubApi.fetchIssuePullRequests(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

export function useCreateIssueBranch(projectId: string, issueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { repoId: string; sourceBranch?: string; customName?: string }) =>
      githubApi.createIssueBranch(projectId, issueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueGitHubKeys.branches(projectId, issueId) });
    },
  });
}

export function useCreateIssuePR(projectId: string, issueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof githubApi.createIssuePR>[2]) =>
      githubApi.createIssuePR(projectId, issueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueGitHubKeys.prs(projectId, issueId) });
    },
  });
}

// --- Review Dashboard hooks ---

export const reviewDashboardKeys = {
  summary: (projectId: string) => ['review-dashboard', 'summary', projectId] as const,
  prs: (projectId: string, params?: Record<string, string>) => ['review-dashboard', 'prs', projectId, params] as const,
  reviewers: (projectId: string) => ['review-dashboard', 'reviewers', projectId] as const,
};

export function useReviewDashboardSummary(projectId: string) {
  return useQuery({
    queryKey: reviewDashboardKeys.summary(projectId),
    queryFn: () => githubApi.fetchReviewDashboardSummary(projectId),
    enabled: !!projectId,
  });
}

export function useReviewDashboardPRs(projectId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: reviewDashboardKeys.prs(projectId, params),
    queryFn: () => githubApi.fetchReviewDashboardPRs(projectId, params),
    enabled: !!projectId,
  });
}

export function useReviewerWorkload(projectId: string) {
  return useQuery({
    queryKey: reviewDashboardKeys.reviewers(projectId),
    queryFn: () => githubApi.fetchReviewerWorkload(projectId),
    enabled: !!projectId,
  });
}

// --- Status Mapping hooks ---

export function useStatusMappings(projectId: string) {
  return useQuery({
    queryKey: githubKeys.statusMappings(projectId),
    queryFn: () => githubApi.fetchStatusMappings(projectId),
    enabled: !!projectId,
  });
}

export function useSaveStatusMappings(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mappings: githubApi.GitHubStatusMappingData[]) =>
      githubApi.saveStatusMappings(projectId, mappings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: githubKeys.statusMappings(projectId) });
    },
  });
}

// --- User profile hooks ---

export function useUserProfile() {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: githubKeys.userProfile(activeOrgId),
    queryFn: githubApi.fetchUserProfile,
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; githubUsername?: string | null }) =>
      githubApi.updateUserProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github', 'user-profile'] });
    },
  });
}
